import { useMutation } from "@apollo/client";
import type { ApolloError, MutationTuple } from "@apollo/client";
import { useTranslation } from "react-i18next";

import {
  CREATE_MAILMAP_ENTRY_MUTATION,
  DELETE_MAILMAP_ENTRY_WITH_SUBENTRIES_MUTATION,
  GET_MAILMAP_RECORDS_QUERY,
  IMPORT_MAILMAP_MUTATION,
  MOVE_MAILMAP_ENTRIES_WITH_SUBENTRIES_MUTATION,
  SET_MAILMAP_ENTRY_AS_MAILMAP_SUBENTRY_MUTATION,
  UPDATE_MAILMAP_ENTRY_MUTATION,
} from "../queries";
import { getConflicts } from "../utils";
import type {
  CreateMailmapEntryMutation,
  DeleteMailmapEntryWithSubentriesMutation,
  Exact,
  ImportMailmapMutation,
  MailmapEntryInput,
  MoveMailmapEntryWithSubentriesMutation,
  SetMailmapEntryAsMailmapSubentryMutation,
  UpdateMailmapEntryMutation,
} from "gql/graphql";
import { Logger } from "utils/logger";
import { msgError, msgSuccess } from "utils/notifications";

function useCreateMailmapEntryMutation(): MutationTuple<
  CreateMailmapEntryMutation,
  Exact<{ entry: MailmapEntryInput; organizationId: string }>
> {
  const { t } = useTranslation();

  return useMutation(CREATE_MAILMAP_ENTRY_MUTATION, {
    onCompleted: (): void => {
      msgSuccess(t("mailmap.successCreateEntry"), t("mailmap.successTitle"));
    },
    onError: (error: ApolloError): void => {
      error.graphQLErrors.forEach((gqlError): void => {
        if (
          gqlError.message.startsWith(
            "Exception - Mailmap entry already exists:",
          )
        ) {
          msgError(
            t("mailmap.errorMailmapEntryAlreadyExists"),
            t("mailmap.errorTitle"),
          );
          Logger.error("Exception - Mailmap entry already exists:", gqlError);
        } else {
          msgError(
            t("mailmap.errorCreateMailmapEntry"),
            t("mailmap.errorTitle"),
          );
          Logger.error("Error creating mailmap entry:", gqlError);
        }
      });
    },
    refetchQueries: [GET_MAILMAP_RECORDS_QUERY],
  });
}

function useDeleteMailmapEntryWithSubentriesMutation(): MutationTuple<
  DeleteMailmapEntryWithSubentriesMutation,
  Exact<{ entryEmail: string; organizationId: string }>
> {
  const { t } = useTranslation();

  return useMutation(DELETE_MAILMAP_ENTRY_WITH_SUBENTRIES_MUTATION, {
    onCompleted: (): void => {
      msgSuccess(t("mailmap.successDeleteEntry"), t("mailmap.successTitle"));
    },
    onError: (error: ApolloError): void => {
      error.graphQLErrors.forEach((gqlError): void => {
        if (
          gqlError.message.startsWith("Exception - Mailmap entry not found:")
        ) {
          msgError(
            t("mailmap.errorDeleteMailmapEntryNotFound"),
            t("mailmap.errorTitle"),
          );
          Logger.error("Exception - Mailmap entry not found:", gqlError);
        } else {
          msgError(
            t("mailmap.errorDeleteMailmapEntry"),
            t("mailmap.errorTitle"),
          );
          Logger.error("Error deleting mailmap entry:", gqlError);
        }
      });
    },
    refetchQueries: [GET_MAILMAP_RECORDS_QUERY],
  });
}

function useUpdateMailmapEntryMutation(): MutationTuple<
  UpdateMailmapEntryMutation,
  Exact<{
    entry: MailmapEntryInput;
    entryEmail: string;
    organizationId: string;
  }>
> {
  const { t } = useTranslation();

  return useMutation(UPDATE_MAILMAP_ENTRY_MUTATION, {
    onCompleted: (): void => {
      msgSuccess(t("mailmap.successUpdateEntry"), t("mailmap.successTitle"));
    },
    onError: (error: ApolloError): void => {
      error.graphQLErrors.forEach((gqlError): void => {
        if (
          gqlError.message.startsWith("Exception - Mailmap entry not found:")
        ) {
          msgError(
            t("mailmap.errorUpdateMailmapEntryNotFound"),
            t("mailmap.errorTitle"),
          );
          Logger.error("Exception - Mailmap entry not found:", gqlError);
        } else {
          msgError(
            t("mailmap.errorUpdateMailmapEntry"),
            t("mailmap.errorTitle"),
          );
          Logger.error("Error updating mailmap entry:", gqlError);
        }
      });
    },
    refetchQueries: [GET_MAILMAP_RECORDS_QUERY],
  });
}

function useSetMailmapEntryAsMailmapSubentryMutation(): MutationTuple<
  SetMailmapEntryAsMailmapSubentryMutation,
  Exact<{
    entryEmail: string;
    organizationId: string;
    targetEntryEmail: string;
  }>
> {
  const { t } = useTranslation();

  return useMutation(SET_MAILMAP_ENTRY_AS_MAILMAP_SUBENTRY_MUTATION, {
    onCompleted: (): void => {
      msgSuccess(
        t("mailmap.successSetEntryAsSubentry"),
        t("mailmap.successTitle"),
      );
    },
    onError: (error: ApolloError): void => {
      error.graphQLErrors.forEach((gqlError): void => {
        if (
          gqlError.message.startsWith("Exception - Mailmap entry not found:")
        ) {
          msgError(
            t("mailmap.errorTargetMailmapEntryNotFound"),
            t("mailmap.errorTitle"),
          );
          Logger.error("Exception - Mailmap entry not found:", gqlError);
        } else {
          msgError(
            t("mailmap.errorSetMailmapEntryAsMailmapSubentry"),
            t("mailmap.errorTitle"),
          );
          Logger.error(
            "Error setting mailmap entry as mailmap subentry",
            gqlError,
          );
        }
      });
    },
    refetchQueries: [GET_MAILMAP_RECORDS_QUERY],
  });
}

function useMoveMailmapEntryWithSubentriesMutation(): MutationTuple<
  MoveMailmapEntryWithSubentriesMutation,
  Exact<{
    entryEmail: string;
    newOrganizationId: string;
    organizationId: string;
  }>
> {
  const { t } = useTranslation();

  return useMutation(MOVE_MAILMAP_ENTRIES_WITH_SUBENTRIES_MUTATION, {
    onCompleted: (): void => {
      msgSuccess(
        t("mailmap.successMoveEntryWithSubentries"),
        t("mailmap.successTitle"),
      );
    },
    onError: (error: ApolloError): void => {
      error.graphQLErrors.forEach((gqlError): void => {
        msgError(
          t("mailmap.errorMoveMailmapEntryWithSubentries"),
          t("mailmap.errorTitle"),
        );
        Logger.error("Error moving mailmap entry with subentries:", gqlError);
      });
    },
    refetchQueries: [GET_MAILMAP_RECORDS_QUERY],
  });
}

function useImportMailmapMutation(): MutationTuple<
  ImportMailmapMutation,
  Exact<{ file: File; organizationId: string }>
> {
  const { t } = useTranslation();

  return useMutation(IMPORT_MAILMAP_MUTATION, {
    onCompleted: (importMailmapResult): void => {
      const { successCount } = importMailmapResult.importMailmap;
      if (successCount === 0) {
        msgError(
          t("mailmap.errorImportExistentMailmap"),
          t("mailmap.errorTitle"),
        );

        return;
      }
      msgSuccess(
        `${t("mailmap.successImportMailmap")}
${t("mailmap.successfullyImportedLines")}: ${successCount}`,
        t("mailmap.successTitle"),
      );
      const messages = importMailmapResult.importMailmap.exceptionsMessages;
      if (messages.length > 0) {
        Logger.warning("Mailmap import exceptions:", messages);
      }
    },
    onError: (error: ApolloError): void => {
      error.graphQLErrors.forEach((gqlError): void => {
        if (
          gqlError.message.startsWith(
            "Exception - Conflicting mappings detected in mailmap file",
          )
        ) {
          const conflicts = getConflicts(gqlError.message);
          msgError(
            `${t("mailmap.errorConflictingMailmapMappingError")}: ${conflicts}`,
            t("mailmap.errorTitle"),
          );
          Logger.error(
            "Exception - Conflicting mappings detected in mailmap file",
            gqlError,
          );
        } else {
          msgError(t("mailmap.errorImportMailmap"), t("mailmap.errorTitle"));
          Logger.error("Error importing mailmap file:", gqlError);
        }
      });
    },
    refetchQueries: [GET_MAILMAP_RECORDS_QUERY],
  });
}
export {
  useCreateMailmapEntryMutation,
  useDeleteMailmapEntryWithSubentriesMutation,
  useImportMailmapMutation,
  useMoveMailmapEntryWithSubentriesMutation,
  useSetMailmapEntryAsMailmapSubentryMutation,
  useUpdateMailmapEntryMutation,
};
